import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { API_BASE } from '../lib/apiConfig';
import GlobalLoader from '../components/context/GlobalLoader';
import SmsConsentTrigger from '../components/SmsConsentTrigger';
import { linkPaidSubscription, loadBillDraft } from '../lib/freeAccount';
import { persistAttributionToProfile } from '../lib/attribution';
import { confirmSignIn } from '../lib/lastSignIn';
import { claimPendingUpload, getPendingUploadId } from '../lib/freeStart';
import { phStartStep } from '../lib/posthog';
import '@styles/components/start-case-paywall.css';

/* Where an account with no plan may go (2026-10-05). They can look around
   and upload a bill. Everything else needs a plan, so it sends them to
   /add-bill (which forwards to their case if they've uploaded).
   Settings and the pages inside it are open too (Oct 2026), so they can
   see plans, add family and reach us before paying. */
/* Pages that don't need the "Open my case" banner: the bill page itself,
   the upload form, Home (it shows the bill), /ask (it pushed the message
   box off a laptop screen) and /bills (the bill is listed there). */
const NO_DRAFT_BANNER = ['/add-bill', '/case/draft', '/', '/dashboard', '/ask', '/bills'];

const FREE_ALLOWED_PATHS = [
  '/', '/dashboard', '/bills', '/add-bill', '/case/draft', '/account', '/how-it-works', '/contact',
  '/settings', '/plan', '/family-members', '/ask',
];

export default function AppGate({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  // Free account with a bill uploaded. Whether the banner shows depends on
  // the page they're on now, worked out at render time. It used to be decided
  // once when the gate ran, so after "Open my case" the banner stayed up on
  // the bill page and tapping it again did nothing (2026-10-27).
  const [hasDraft, setHasDraft] = useState(false);
  const showDraftBanner = hasDraft && !NO_DRAFT_BANNER.includes(location.pathname);

  useEffect(() => {
    let cancelled = false;

    const gate = async () => {
      /* ===============================
         0) MAGIC-LINK AUTO-AUTH
         If the URL has ?mlt=<token>, exchange it for a Supabase session
         BEFORE the auth check. This bypasses the OTP flow for users who
         arrived via a chat-notification email. Falls through silently to
         the normal auth path if the token is missing, expired, or used.
      =============================== */
      const url = new URL(window.location.href);

      /* A Google sign-in for an email with no account gets rejected by
         Supabase (public signups are off since 2026-08-26) and bounces
         back here with the rejection in the URL fragment and no session.
         Turn that into the "no account found" message Login already knows
         how to show, instead of a silent kick to an empty login form. */
      if (url.hash.includes('error_code=signup_disabled')) {
        // Coming from "Sign up with Google" the login message would be
        // wrong, so send them back to sign up with email instead.
        const fromSignup = url.searchParams.get('from') === 'signup';
        navigate(fromSignup ? '/signup?error=google_unavailable' : '/login?error=no_account_found', {
          replace: true,
        });
        return;
      }

      const mlt = url.searchParams.get('mlt');

      // After consume, the server returns the `redirect_path` that was
      // bound to the token at issue time. SMS short URLs land users at
      // `/` (compact body) and rely on this to push them into the case
      // page. Email links already encode the full path so the redirect
      // is a harmless no-op for those.
      let postConsumeRedirect: string | null = null;

      if (mlt) {
        try {
          const res = await fetch(`${API_BASE}/auth/magic-link/consume`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: mlt }),
          });
          if (res.ok) {
            const { hashed_token, redirect_path } = (await res.json()) as {
              hashed_token: string;
              redirect_path?: string | null;
            };
            await supabase.auth.verifyOtp({
              token_hash: hashed_token,
              type: 'magiclink',
            });
            postConsumeRedirect = redirect_path ?? null;
            console.log('✅ AppGate: magic-link auto-auth succeeded');
          } else {
            console.warn('⚠️ AppGate: magic-link consume returned', res.status);
          }
        } catch (err) {
          console.warn('⚠️ AppGate: magic-link consume failed', err);
        }
        // Always strip the token from the URL so it's never preserved in
        // history or subsequent navigation. Whether or not the consume
        // succeeded, the token is now spent.
        url.searchParams.delete('mlt');
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);

        // If the redirect path the token was issued for is different
        // from where we are right now, navigate there. Skip same-path
        // navigations to avoid an unnecessary re-render.
        if (postConsumeRedirect && postConsumeRedirect !== url.pathname + url.search) {
          navigate(postConsumeRedirect, { replace: true });
          // Continue the gate, auth check below still has to run on
          // the new route, but `useEffect` doesn't re-fire from a
          // navigate within the same render. Falling through here is
          // safe because the auth/subscription/onboarding checks are
          // path-agnostic.
        }
      }

      if (cancelled) return;

      /* ===============================
         1) AUTH
      =============================== */
      const {
        data: { session },
        error: authError,
      } = await supabase.auth.getSession();

      if (cancelled) return;

      if (authError || !session?.user) {
        navigate('/login', { replace: true });
        return;
      }

      const user = session.user;

      // They're in, so the method they just used gets the "Last used" tag.
      confirmSignIn();

      /* Save where they came from (utm_*, referrer, landing page) onto the
         profile. Email signups did this on /verify, but Google signups skip
         /verify and land straight here, so their source was never saved
         (Oscar and Kennady, Oct 2026). It's first-touch and never
         overwrites, so running it once per visit is safe. */
      try {
        if (sessionStorage.getItem('attribution_saved') !== '1') {
          sessionStorage.setItem('attribution_saved', '1');
          void persistAttributionToProfile(user.id);
        }
      } catch {
        void persistAttributionToProfile(user.id);
      }

      /* Google signups land here with ?from=signup and never touch the
         server, so the "new free account" email to Flavia never went out
         for them. Ping it once. The server checks it's a new Google
         account and only sends the alert the first time. */
      if (url.searchParams.get('from') === 'signup' && session.access_token) {
        void fetch(`${API_BASE}/notify/free-signup`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}` },
        }).catch(() => {});
      }

      /* ===============================
         2) PRE-LINK ANY ORPHAN SUBSCRIPTION
         Always attempt to link if checkout_ref is in localStorage  
         covers both first-time signups AND returning users who made
         a second purchase. The server endpoint is idempotent: a no-op
         if the ref doesn't match an unlinked subscription.
      =============================== */
      const checkoutRef = localStorage.getItem('checkout_ref');

      if (checkoutRef && session?.access_token) {
        try {
          const res = await fetch(`${API_BASE}/stripe/link-subscription`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ checkout_ref: checkoutRef }),
          });

          if (res.ok) {
            console.log('✅ AppGate: linked subscription via checkout_ref');
          } else {
            console.warn('⚠️ AppGate: link-subscription returned non-ok', res.status);
          }
        } catch (err) {
          console.warn('⚠️ AppGate: link-subscription request failed', err);
        }

        // Always clear the ref + plan + short_id, we only get one shot per
        // checkout. The plan and short_id are read by AuthSidecar during
        // signup/verify/onboarding to keep the case-file panel populated,
        // so they live alongside the ref.
        localStorage.removeItem('checkout_ref');
        localStorage.removeItem('checkout_plan');
        localStorage.removeItem('checkout_short_id');
      }

      if (cancelled) return;

      /* ===============================
         2b) A BILL UPLOADED ON /start BEFORE THEY HAD AN ACCOUNT
         (2026-10-16). It becomes their waiting bill now, and they go
         straight to its first look.
      =============================== */
      if (getPendingUploadId() && session.access_token) {
        const claimed = await claimPendingUpload(session.access_token);
        if (cancelled) return;
        if (claimed) {
          phStartStep('upload_claimed');
          if (window.location.pathname !== '/case/draft') {
            // The gate runs again on /case/draft, so stop this one here.
            navigate('/case/draft', { replace: true });
            return;
          }
        }
      } else if (session.access_token) {
        // Since 2026-11-03 the server also attaches uploads by the email on
        // them (the code email opened on another phone, or the link in a
        // follow-up email). Asked once per tab, in the background so it
        // never slows the page down. Almost always there's nothing.
        const claimKey = `ml_claim_checked:${user.id}`;
        let checked = false;
        try {
          checked = sessionStorage.getItem(claimKey) === '1';
          sessionStorage.setItem(claimKey, '1');
        } catch {
          /* no sessionStorage, ask every time */
        }
        if (!checked) {
          void claimPendingUpload(session.access_token).then((claimed) => {
            if (!claimed || cancelled) return;
            phStartStep('upload_claimed', { by: 'email' });
            if (window.location.pathname !== '/case/draft') navigate('/case/draft', { replace: true });
          });
        }
      }

      if (cancelled) return;

      /* ===============================
         3) SUBSCRIPTION CHECK
         Take the most recent subscription. Users may have multiple
         (e.g. annual + one_off second purchase), so we can't use
         maybeSingle(), it errors on >1 row.
      =============================== */
      const { data: subscriptions, error: subError } = await supabase
        .from('subscriptions')
        .select('id, status, plan, bills_used, bills_limit')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (cancelled) return;

      let subscription = subscriptions?.[0];

      if (subError) {
        await supabase.auth.signOut();
        navigate('/login?error=no_account_found', { replace: true });
        return;
      }

      /* ===============================
         3b) NO PLAN = FREE ACCOUNT (2026-10-05)
         This used to sign the user out. Now an account without a plan
         gets into the portal and can upload a bill, and pays when they
         want us to start. Before deciding they're free, try once more to
         claim a payment by email, which covers someone who paid and came
         back on another device (no checkout_ref in localStorage here).
      =============================== */
      if (!subscription) {
        const linked = await linkPaidSubscription(user.id, session.access_token ?? null);
        if (cancelled) return;

        if (linked) {
          const { data: again } = await supabase
            .from('subscriptions')
            .select('id, status, plan, bills_used, bills_limit')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1);
          if (cancelled) return;
          subscription = again?.[0];
        }
      }

      if (!subscription) {
        const path = window.location.pathname;
        if (!FREE_ALLOWED_PATHS.includes(path)) {
          // Leave the loader up, this gate unmounts with the navigation.
          // No popup on arrival (F, 2026-10-05): they land on their case,
          // or the upload form, and the button there is the only ask.
          navigate('/add-bill', { replace: true });
          return;
        }

        // Where a free account lands (Oct 2026). One job per visit: no
        // bill yet means upload one, a bill waiting means start the case.
        // So the first time they hit Home in a visit they go to that job,
        // straight to the bill if there is one (it used to hop through
        // /add-bill first). After that Home is theirs to look around.
        // Links to something specific (a case, Settings) aren't touched.
        if (path === '/') {
          let landed = false;
          try {
            landed = sessionStorage.getItem('free_landed') === '1';
            sessionStorage.setItem('free_landed', '1');
          } catch {
            landed = true;
          }
          if (!landed) {
            const parked = await loadBillDraft(user.id);
            if (cancelled) return;
            navigate(parked ? '/case/draft' : '/add-bill', { replace: true });
            return;
          }
        }

        // Keep the parked bill one tap away while they look around.
        // See NO_DRAFT_BANNER for the pages that leave it out.
        {
          const parked = await loadBillDraft(user.id);
          if (cancelled) return;
          setHasDraft(Boolean(parked));
        }

        setLoading(false);
        return;
      }

      /* ===============================
         4) ONBOARDING CHECK
      =============================== */
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('onboarding_completed, welcome_email_sent_at')
        .eq('id', user.id)
        .maybeSingle();

      if (cancelled) return;

      if (profileError) {
        navigate('/login', { replace: true });
        return;
      }

      /* ===============================
         4b) ENSURE WELCOME EMAIL
         The welcome used to fire only from the checkout_ref link path, so
         returning members (linked via Verify's email fallback) never got
         one. AppGate runs after every login regardless of linking path, so
         trigger it here — gated on a null flag so it runs at most once per
         user. Fire-and-forget: an email send must never block the gate, and
         the server also dedupes via profiles.welcome_email_sent_at.
      =============================== */
      if (profile && !profile.welcome_email_sent_at && session.access_token) {
        void fetch(`${API_BASE}/stripe/ensure-welcome`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
        }).catch((err) => {
          console.warn('⚠️ AppGate: ensure-welcome request failed', err);
        });
      }

      if (!profile || !profile.onboarding_completed) {
        navigate('/onboarding', { replace: true });
        return;
      }

      /* ===============================
         5) PLAN-SCOPED REDIRECTS
         EOB customers go to /eob and Personalized Action Plan customers to
         /action-plan, not /dashboard. Neither plan has bill cases, so
         the case surfaces would be empty for them.
      =============================== */
      const currentPath = window.location.pathname;
      const isEobPlan = subscription?.plan === 'eob';
      const EOB_ALLOWED_PATHS = ['/eob', '/ask', '/settings', '/plan', '/account', '/contact', '/how-it-works'];
      if (isEobPlan && !EOB_ALLOWED_PATHS.includes(currentPath)) {
        navigate('/eob', { replace: true });
        return;
      }
      const isActionPlan = subscription?.plan === 'action_plan';
      const ACTION_PLAN_ALLOWED_PATHS = ['/action-plan', '/ask', '/settings', '/plan', '/account', '/contact', '/how-it-works'];
      if (isActionPlan && !ACTION_PLAN_ALLOWED_PATHS.includes(currentPath)) {
        navigate('/action-plan', { replace: true });
        return;
      }

      /* ===============================
         6) A BILL UPLOADED BEFORE PAYING
         Someone who uploaded on a free account and then paid lands on
         Home with their bill still parked. Take them to /add-bill,
         which has it attached and ready to send. Only checked on
         Home (/) so every other page costs nothing extra.
      =============================== */
      // Only when they have a bill left to use it on. Someone who has used
      // all their bills would land on the "used all your bills" card, close
      // it, come back Home and get sent straight back to it.
      const billsLeft =
        typeof subscription?.bills_limit === 'number' &&
        (subscription.bills_used ?? 0) < subscription.bills_limit;
      if (currentPath === '/' && billsLeft) {
        const draft = await loadBillDraft(user.id);
        if (cancelled) return;
        if (draft) {
          navigate('/add-bill', { replace: true });
          return;
        }
      }

      /* ===============================
         ✅ ACCESS GRANTED
      =============================== */
      setLoading(false);
    };

    gate();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (loading) return <GlobalLoader message="Loading your account…" />;

  return (
    <>
      {showDraftBanner && (
        <div className="free-draft-banner" role="status">
          <span>Your bill is saved. Nothing happens to it until you start your case.</span>
          <button type="button" onClick={() => navigate('/case/draft')}>
            Open my case
          </button>
        </div>
      )}
      {children}
      {/* Self-decides whether to show the SMS consent modal, only renders
          for users with a phone on file who haven't yet been prompted. */}
      <SmsConsentTrigger />
    </>
  );
}
